
import { useState, useEffect } from 'react';
import { useIsMobile } from './use-mobile';

interface ResponsiveChartDimensions {
  height: number;
  containerClassName: string;
  shouldScroll: boolean;
}

export const useResponsiveCharts = (
  dataLength: number = 0,
  chartType: 'bar' | 'pie' | 'line' = 'bar'
): ResponsiveChartDimensions => {
  const isMobile = useIsMobile();
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => setScreenHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate optimal height based on screen size and data
  const calculateHeight = (): number => {
    if (chartType === 'pie') {
      return isMobile ? Math.min(280, screenHeight * 0.35) : 350;
    }

    if (chartType === 'line') {
      return isMobile ? Math.min(250, screenHeight * 0.3) : 300;
    }

    // Bar charts - mobile-optimized heights
    const baseHeight = isMobile ? 250 : 400;
    const heightPerItem = isMobile ? 25 : 40;
    const maxHeight = isMobile ? screenHeight * 0.6 : screenHeight * 0.8;
    
    // For small datasets, use base height. For larger ones, scale appropriately
    if (dataLength <= 3) {
      return baseHeight;
    }
    
    const calculatedHeight = Math.max(baseHeight, dataLength * heightPerItem);
    return Math.min(calculatedHeight, maxHeight);
  };

  const height = calculateHeight();
  
  // On mobile, we generally don't want horizontal scrolling for charts
  // Instead, we make them fit the screen width
  const shouldScroll = false; // Disabled horizontal scrolling for better mobile UX

  const containerClassName = shouldScroll 
    ? 'overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100'
    : '';

  return {
    height,
    containerClassName,
    shouldScroll
  };
};
